import * as React from "react";
import { toast } from "react-toastify";
import { useToggle } from "react-use";
import { useAppDispatch } from "@/store/hooks";
import { BUSINESS_REPORTS_TAGS } from "@/modules/snapshots/businessReports.constraints";
import {
  businessReportsApi,
  useCreateAiColumnMutation,
  useLazyGetAiColumnProgressQuery,
} from "@/modules/snapshots/businessReports.api";
import type { PromptEditFormInputs } from "@/modules/snapshots/components/BusinessReportPromptEditModal.utils";

export const AI_COLUMN_PROGRESS_POLL_INTERVAL_MS = 4000;

interface Input {
  collectionId: string | undefined;
  businessReportId: string | undefined;
  refetch: () => unknown;
}

/** "Add column" flow: creates an AI column, then polls its progress and refreshes the grid until it finishes. */
export const useAiColumnGeneration = ({ collectionId, businessReportId, refetch }: Input) => {
  const dispatch = useAppDispatch();
  const [isAddColumnOpen, toggleAddColumnOpen] = useToggle(false);
  const [createAiColumn, { isLoading: isAddingColumn }] = useCreateAiColumnMutation();
  const [generatingColumn, setGeneratingColumn] = React.useState<string | null>(null);
  const [fetchAiColumnProgress] = useLazyGetAiColumnProgressQuery();

  // The grid is refetched only when the pending count moved since the previous tick (cells landed),
  // and a hidden tab skips the tick: polling must not duplicate the WebSocket refresh every 4 s.
  const lastPending = React.useRef<number | null>(null);
  React.useEffect(() => {
    if (!generatingColumn || !collectionId) return;
    let cancelled = false;
    lastPending.current = null;
    const interval = window.setInterval(async () => {
      if (document.hidden) return;
      try {
        const { pending } = await fetchAiColumnProgress({
          snapshotCollectionId: collectionId,
          name: generatingColumn,
        }).unwrap();
        if (cancelled) return;
        if (pending !== lastPending.current) {
          lastPending.current = pending;
          refetch();
        }
        if (pending === 0) {
          setGeneratingColumn(null);
          dispatch(businessReportsApi.util.invalidateTags([BUSINESS_REPORTS_TAGS.PIVOT_FIELD_SCHEMA_TAG]));
        }
      } catch {
        if (!cancelled) setGeneratingColumn(null);
      }
    }, AI_COLUMN_PROGRESS_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [generatingColumn, collectionId, fetchAiColumnProgress, refetch, dispatch]);

  const handleAddColumn = async (data: PromptEditFormInputs) => {
    if (!collectionId || !businessReportId) return;
    try {
      const created = await createAiColumn({
        snapshotCollectionId: collectionId,
        businessReportId,
        name: data.name,
        prompt: data.prompt,
        full_context: data.full_context,
        is_date: data.is_date,
        data_type: data.data_type,
        use_image: data.use_image,
        no_retriever: data.no_retriever,
        web_search: data.web_search,
        traffic_light: data.traffic_light,
      }).unwrap();
      setGeneratingColumn(created.name);
      toast.success(`Generating "${created.name}" across the collection`);
    } catch (error) {
      const apiError = error as { data?: { detail?: string } };
      toast.error(apiError?.data?.detail ?? "Failed to add column");
      throw error;
    }
  };

  return { isAddColumnOpen, toggleAddColumnOpen, isAddingColumn, handleAddColumn };
};
